import {
    AlertTriangle,
    ArrowRight,
    X,
    LoaderCircle,
} from 'lucide-react'
import React, { useContext, useState } from 'react'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './RejectAdvanceModal.module.css'

const QUICK_REASONS = [
    'Active advance already outstanding',
    'Amount exceeds policy',
    'Salary insufficient for EMI',
    'Staff on notice period',
    'Policy violation',
]

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatINR = (amount) => {
    const num = parseFloat(amount)
    if (!isFinite(num)) return '\u2014'
    return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

const RejectAdvanceModal = ({ isOpen, advance, onClose, onSuccess }) => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [rejectionReason, setRejectionReason] = useState('')
    const [reasonError, setReasonError] = useState(null)
    const [isRejecting, setIsRejecting] = useState(false)

    const resetForm = () => {
        setRejectionReason('')
        setReasonError(null)
    }

    const handleClose = () => {
        resetForm()
        onClose()
    }

    const selectQuickReason = (reason) => {
        setRejectionReason(reason)
        setReasonError(null)
    }

    const rejectAdvance = async () => {
        if (!rejectionReason.trim()) {
            setReasonError('Provide a reason for rejecting this advance.')
            return
        }
        setReasonError(null)

        if (!authCtx?.token || !activeStore?.storeIdentifier || !advance?.advanceIdentifier) {
            toast.error('Missing required data.')
            return
        }

        try {
            setIsRejecting(true)

            const response = await fetch(BASE_URL_API + 'rejectStaffAdvanceRequest', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    advanceIdentifier: advance.advanceIdentifier,
                    rejectedBy: authCtx?.authIdentifier || authCtx?.staffIdentifier || '',
                    rejectionReason: rejectionReason.trim(),
                }),
            })

            const respo = await response.json()

            if (respo?.status === 'success') {
                toast.success(
                    `Advance request rejected. ${respo.response?.scheduleRowsVoided || 0} schedule row(s) voided.`
                )
                handleClose()
                if (onSuccess) onSuccess()
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'ADV_001':
                    toast.error('Advance not found.')
                    handleClose()
                    break
                case 'ADV_002':
                    toast.error(
                        `Cannot reject — advance is already "${respo?.response?.currentStatus || 'updated'}".`
                    )
                    handleClose()
                    break
                case 'VAL_001':
                    setReasonError(respo?.message || 'Provide a reason.')
                    break
                default:
                    toast.error(respo?.message || 'Rejection failed.')
            }
        } catch (error) {
            console.error('rejectAdvance error:', error)
            toast.error('Network error.')
        } finally {
            setIsRejecting(false)
        }
    }

    if (!isOpen) return null

    return (
        <>
            {/* Overlay */}
            <div className={styles.overlay} onClick={handleClose} />

            {/* Modal */}
            <div className={styles.modal}>
                {/* Header */}
                <div className={styles.header}>
                    <div className={styles.headerContent}>
                        <h2 className={styles.title}>Reject Advance Request</h2>
                        <p className={styles.subtitle}>
                            {advance?.staffName || 'Staff'} — ₹{formatINR(advance?.requestedAmount)}
                        </p>
                    </div>
                    <button
                        type="button"
                        className={styles.closeBtn}
                        onClick={handleClose}
                        disabled={isRejecting}
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body */}
                <div className={styles.body}>
                    {/* Reason Field */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Rejection Reason *</label>
                        <textarea
                            className={`${styles.reasonInput} ${reasonError ? styles.inputError : ''}`}
                            placeholder="e.g. Active advance already outstanding, Amount too high, Salary too low for EMI"
                            value={rejectionReason}
                            onChange={(e) => {
                                setRejectionReason(e.target.value)
                                setReasonError(null)
                            }}
                            rows={3}
                            disabled={isRejecting}
                        />
                        {reasonError && <span className={styles.errorText}>{reasonError}</span>}
                    </div>

                    {/* Quick Reasons */}
                    <div className={styles.quickReasonsSection}>
                        <span className={styles.quickReasonsLabel}>Quick select:</span>
                        <div className={styles.quickReasons}>
                            {QUICK_REASONS.map((reason) => (
                                <button
                                    key={reason}
                                    type="button"
                                    className={styles.quickReasonBtn}
                                    onClick={() => selectQuickReason(reason)}
                                    disabled={isRejecting}
                                    title={reason}
                                >
                                    {reason}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <button
                        type="button"
                        className={styles.cancelBtn}
                        onClick={handleClose}
                        disabled={isRejecting}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={styles.rejectBtn}
                        onClick={rejectAdvance}
                        disabled={isRejecting}
                    >
                        {isRejecting ? (
                            <>
                                <LoaderCircle size={14} className={styles.spinIcon} /> Rejecting...
                            </>
                        ) : (
                            <>
                                Reject Request <ArrowRight size={14} />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </>
    )
}

export default RejectAdvanceModal
